import React from "react";
import { useCart } from "../../lib/cart";
import { X, Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { money, api, formatApiError } from "../../lib/api";
import { resolveUnitPrice } from "../../lib/pricing";
import { thumbnailUrl } from "../../lib/imgSrcSet";
import SafeImage from "./SafeImage";
import CouponField from "./CouponField";
import { PaymentMethods, PolicyLinksRow, PaymentOptionsNote } from "./TrustBadges";
import { toast } from "sonner";

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, subtotal, clear, coupon, discount, total, shippingMethods, shippingRequired, shippingMethodId, setShippingMethodId, shippingCost } = useCart();
  const [loading, setLoading] = React.useState(false);
  const [notes, setNotes] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [phone, setPhone] = React.useState("");

  const handleCheckout = async () => {
    if (!items.length) return;
    if (shippingRequired && !shippingMethodId) {
      toast.error("Please select a shipping method");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        origin_url: window.location.origin,
        notes: notes.trim() || null,
        customer_email: email.trim() || null,
        customer_phone: phone.trim() || null,
        coupon_code: coupon?.valid ? coupon.code : null,
        shipping_method_id: shippingRequired ? shippingMethodId : null,
      });
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        data-testid="cart-overlay"
        className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        onClick={() => setOpen(false)}
      />
      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-50 h-full h-[100dvh] max-h-[100dvh] w-full max-w-md bg-[#0a0a0a] border-l border-white/10 shadow-2xl flex flex-col transform transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="shrink-0 flex items-center justify-between px-6 py-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ShoppingBag size={18} className="text-zinc-300" />
            <h2 className="font-display text-lg tracking-tight">Your Cart</h2>
          </div>
          <button data-testid="cart-close-btn" onClick={() => setOpen(false)} className="p-2 hover:bg-white/5 rounded">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="text-center text-zinc-500 py-20">
              <ShoppingBag size={48} className="mx-auto mb-4 opacity-30" />
              <p className="text-sm">Your cart is empty.</p>
            </div>
          ) : (
            <>
            <div className="space-y-4">
              {items.map((it) => {
                const unit = resolveUnitPrice(it.price, it.price_tiers, it.quantity);
                const tiered = unit < Number(it.price);
                return (
                <div key={it.product_id} data-testid={`cart-item-${it.sku}`} className="flex gap-4 py-3 border-b border-white/5">
                  {it.image ? (
                    <SafeImage src={thumbnailUrl(it.image)} alt={it.name} className="w-20 h-20 object-contain rounded bg-white border border-zinc-200/70 p-1" loading="lazy" fallbackLabel="" />
                  ) : (
                    <div className="w-20 h-20 rounded bg-zinc-900" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{it.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{it.sku}</div>
                    {tiered && (
                      <div className="text-[10px] text-emerald-500 font-mono mt-0.5" data-testid={`cart-tier-note-${it.sku}`}>
                        Volume price {money(unit)} ea
                      </div>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      <button data-testid={`cart-decr-${it.sku}`} onClick={() => updateQty(it.product_id, it.quantity - 1)} className="w-7 h-7 rounded border border-white/10 flex items-center justify-center hover:bg-white/5">
                        <Minus size={12} />
                      </button>
                      <span className="text-sm w-6 text-center font-mono">{it.quantity}</span>
                      <button data-testid={`cart-incr-${it.sku}`} onClick={() => updateQty(it.product_id, it.quantity + 1)} className="w-7 h-7 rounded border border-white/10 flex items-center justify-center hover:bg-white/5">
                        <Plus size={12} />
                      </button>
                      <button
                        data-testid={`cart-remove-${it.sku}`}
                        onClick={() => remove(it.product_id)}
                        aria-label={`Remove ${it.name} from cart`}
                        title="Remove item"
                        className="ml-auto inline-flex items-center gap-1 h-7 px-2 rounded border border-white/10 text-xs text-zinc-300 hover:text-white hover:border-red-500/60 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 size={13} aria-hidden="true" /> Remove
                      </button>
                    </div>
                  </div>
                  <div className="text-sm font-mono">{money(unit * it.quantity)}</div>
                </div>
              );})}
            </div>
            <div data-testid="cart-notes-block" className="space-y-3 mt-5">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="email"
                  data-testid="cart-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-red-500"
                />
                <input
                  type="tel"
                  data-testid="cart-phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone"
                  className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-red-500"
                />
              </div>
              <div>
                <label htmlFor="cart-notes" className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold block mb-2">
                  Order notes or special remarks
                </label>
                <textarea
                  id="cart-notes"
                  data-testid="cart-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. leave at front door, VIN, install date"
                  className="w-full px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-red-500 resize-none"
                />
                <div className="text-[10px] text-zinc-600 mt-1 text-right font-mono">{notes.length}/500</div>
              </div>
            </div>
            <div data-testid="cart-coupon-field" className="mt-3"><CouponField /></div>
            {shippingRequired && (
            <div data-testid="cart-shipping-methods" className="mt-3 border border-white/10 rounded-sm p-3 space-y-2">
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">Shipping method</div>
              {shippingMethods.map((m) => (
                <label key={m.id} data-testid={`cart-ship-method-${m.id}`} className={`flex items-center justify-between gap-3 px-3 py-2 rounded-sm border cursor-pointer transition ${shippingMethodId === m.id ? "border-red-500 bg-red-500/10" : "border-white/10 hover:border-white/30"}`}>
                  <span className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="cart-shipping-method"
                      data-testid={`cart-ship-radio-${m.id}`}
                      checked={shippingMethodId === m.id}
                      onChange={() => setShippingMethodId(m.id)}
                      className="accent-red-600"
                    />
                    {m.name}
                  </span>
                  <span className="font-mono text-sm">{m.cost > 0 ? money(m.cost) : "Free"}</span>
                </label>
              ))}
            </div>
            )}
            </>
          )}
        </div>

        <div className="shrink-0 border-t border-white/10 px-6 py-5 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-400">Subtotal</span>
            <span data-testid="cart-subtotal" className="font-mono text-base">{money(subtotal)}</span>
          </div>
          {discount > 0 && (
            <div className="flex items-center justify-between text-sm text-emerald-400" data-testid="cart-discount-row">
              <span>Discount {coupon?.code ? `(${coupon.code})` : ""}</span>
              <span className="font-mono" data-testid="cart-discount-amount">−{money(discount)}</span>
            </div>
          )}
          {shippingRequired && (
            <div className="flex items-center justify-between text-sm" data-testid="cart-shipping-row">
              <span className="text-zinc-400">Shipping</span>
              <span className="font-mono" data-testid="cart-shipping-cost">
                {shippingMethodId ? (shippingCost > 0 ? money(shippingCost) : "Free") : "Select a method"}
              </span>
            </div>
          )}
          {(discount > 0 || shippingRequired) && (
            <div className="flex items-center justify-between text-sm border-t border-white/10 pt-2">
              <span className="text-zinc-300 font-medium">Total</span>
              <span data-testid="cart-total" className="font-mono text-base">{money(total)}</span>
            </div>
          )}
          {!shippingRequired && (
            <p className="text-[11px] text-zinc-500">Shipping is calculated at checkout. No additional taxes or hidden fees are added.</p>
          )}
          <button
            data-testid="cart-checkout-btn"
            disabled={!items.length || loading || (shippingRequired && !shippingMethodId)}
            onClick={handleCheckout}
            className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-medium tracking-wide transition disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
          >
            {loading ? "Redirecting…" : "Secure Checkout"}
          </button>
          {items.length > 0 && (
            <>
              <PaymentMethods className="pt-1" />
              <PaymentOptionsNote />
              <PolicyLinksRow />
            </>
          )}
          {items.length > 0 && (
            <button data-testid="cart-clear-btn" onClick={clear} className="w-full text-xs text-zinc-500 hover:text-zinc-300">
              Clear cart
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
